import { Button, message, Select, Table, TableColumnsType, TableProps, Typography } from "antd";
import { useEffect, useState } from "react";
import { Filter, Month, OptionFilter, ReportChemicalVolume } from "src/entity/terminal-marketing/terminal-marketing.entity";
import useNavigationMenu from "src/hook/useNavigationMenu";
import { getOptions, getReportChemicalVolume, getYear } from "src/services/terminal-marketing.service";
import { excelStore } from "src/stores/excel-store.interface";
import { profileStore } from "src/stores/profile-store";
import { formatNumber } from "src/utils/process-number";
import { useShallow } from "zustand/react/shallow";
import { canViewTable } from "../../PermissionCheck/PermissionCheck";
import { terminalStore } from "src/stores/terminal-store";
import { PiChartBarFill } from "react-icons/pi";
import { BsTable } from "react-icons/bs";
import { componentPalette } from "src/theme/component-palette";
import { companyStore } from "src/stores/company-store";
import { generateYearArray } from "src/utils/array";

type OnChange = NonNullable<TableProps<any>["onChange"]>;
type Filters = Parameters<OnChange>[1];
const { Text } = Typography;

const ReportChemicalVolumeTable = () => {
  const profile = profileStore(useShallow((state) => state.profile));
  const { permissions } = useNavigationMenu();
  const [companyId, setCompanyId] = useState<string>("");
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [messageApi, contextHolder] = message.useMessage();
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [optionYears, setOptionYears] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<Filters>({});
  const [data, setData] = useState<ReportChemicalVolume[]>([]);
  const currentYear = new Date().getFullYear();
  const [optionsFilter, setOptionFilter] = useState<OptionFilter[]>([]);
  const [selectedButton, setSelectedButton] = useState<string>("table");
  const loadingProfile = profileStore(useShallow((state) => state.loading));

  const excelSetSelectedYear = excelStore(useShallow((state) => state.setSelectedYear));
  const excelSetQueryStr = excelStore(useShallow((state) => state.setQueryStr));

  const getChemicalProperty = terminalStore(useShallow((state) => state.getChemicalProperty));
  const chemicalPropertyMaster = terminalStore(useShallow((state) => state.chemicalProperty));

  const getLoadingMethod = terminalStore(useShallow((state) => state.getLoadingMethod));
  const loadingMethodMaster = terminalStore(useShallow((state) => state.loadingMethod));

  const getAreaByCompanyId = companyStore(useShallow((state) => state.getAreaByCompanyId));
  const areaMaster = companyStore(useShallow((state) => state.dataArea));

  const fetchYear = () => {
    const years = generateYearArray();
    setOptionYears(years as number[]);
    setSelectedYear(years[0] as number);
  };

  const handleYearChange = (value: number) => {
    setSelectedYear(value);
  };

  const handleButtonMenuClick = (buttonName: string) => {
    setSelectedButton(buttonName);
  };

  const calPagination = (current?: number) => {
    if (current) {
      return (current - 1) * 10;
    }
  };

  const createParamFilter = (type: "fetch" | "export") => {
    let queryParam = "";
    let offset = 0;
    let limit = pageSize;
    if (selectedYear) {
      queryParam += `year=${selectedYear}`;
    }
    if (type === "fetch") {
      if (current) {
        let resultCalOffset = calPagination(current);
        if (resultCalOffset) {
          offset = resultCalOffset;
        }
        queryParam += `&offset=${offset}`;
      }
      if (limit) {
        queryParam += `&limit=${limit}`;
      }
    }

    if (filter) {
      const str = Object.entries(filter)
        .filter(([, value]) => value)
        .map(([key, value]) => `&${key}=[${value?.join(",")}]`)
        .join("");
      queryParam += str;
    }

    return queryParam;
  };

  const fetchReportChemVolume = async () => {
    try {
      if (companyId && selectedYear) {
        setLoading(true);
        let offset = 0;
        let resultCalOffset = calPagination(current);
        if (resultCalOffset) {
          offset = resultCalOffset;
        }
        const query = createParamFilter("fetch");
        const response = await getReportChemicalVolume(companyId, query);
        if (response.data.code === 200) {
          setLoading(false);
          const data = response.data.data.data;
          const total = response.data.data.total;
          let tmpData = data.map((el: any, index: number) => {
            return {
              no: (index + offset + 1).toString(),
              ...el,
            };
          });
          setData([...tmpData]);
          setTotal(total);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOption = async () => {
    try {
      if (companyId && selectedYear) {
        const response = await getOptions(companyId, selectedYear);
        if (response.data.code === 200) {
          const data = response.data.data.filter;
          setOptionFilter(data);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const mapFilter = (key: string): Filter[] | [] => {
    const resultFilter = optionsFilter.find((item) => item.key === key)?.filter;
    if (resultFilter) {
      const filter: Filter[] = resultFilter?.map((value: any) => {
        let text = value;
        if (key === "area") {
          text = getMasterLabel("area", text);
        } else if (key === "chemicalId") {
          text = getMasterLabel("chemicalId", text);
        } else if (key === "loadingById") {
          text = getMasterLabel("loadingById", text);
        }
        return {
          text,
          value: value,
        };
      });
      return filter;
    } else {
      return [];
    }
  };

  const onTableChange: TableProps<ReportChemicalVolume>["onChange"] = (pagination: any, filters: any) => {
    let current = pagination.current;
    setFilter(filters);
    setCurrent(current);
  };

  const buttonStyle = (buttonName: string) => ({
    backgroundColor: selectedButton === buttonName ? componentPalette.primary : "initial",
    color: selectedButton === buttonName ? "white" : "initial",
  });

  const intitalData = () => {
    if (profile?.activeCompany?._id) {
      setCompanyId(profile.activeCompany._id);
    }
  };

  const resetFilter = () => {
    setFilter({});
  };

  const initMaster = async () => {
    try {
      const query = "";
      setLoading(true);
      await Promise.all([getChemicalProperty(query), getLoadingMethod(query)]);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    intitalData();
    initMaster();
    fetchYear();
  }, []);

  useEffect(() => {
    intitalData();
  }, [profile?.activeCompany]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setSelectedYear(currentYear);
        await Promise.all([getAreaByCompanyId(companyId)]);
      } catch (error) {
        console.error(error);
      }
    };
    fetchData();
    setCurrent(1);
  }, [companyId]);

  useEffect(() => {
    if (loadingProfile === true) {
      setLoading(true);
    }
  }, [loadingProfile]);

  useEffect(() => {
    resetFilter();
    setCurrent(1);
  }, [selectedYear]);

  useEffect(() => {
    const fetchData = async () => {
      if (companyId && selectedYear) {
        await Promise.all([fetchReportChemVolume(), fetchOption()]);
      }
    };
    fetchData();
  }, [current, filter]);

  useEffect(() => {
    if (selectedYear) {
      excelSetSelectedYear(selectedYear);
    }
  }, [selectedYear]);

  useEffect(() => {
    const queryParam = createParamFilter("export");
    if (queryParam) {
      excelSetQueryStr(queryParam);
    }
  }, [filter, selectedYear]);

  const getMasterLabel = (type: string, text: string) => {
    if (!text || typeof text != "string") return "";
    if (type === "area" && areaMaster) {
      const optValue = areaMaster.find((item) => item._id === text);
      return optValue?.areaName;
    } else if (type === "chemicalId" && chemicalPropertyMaster) {
      const optValue = chemicalPropertyMaster.find((item) => item._id === text);
      return optValue?.chemicalName;
    } else if (type === "loadingById" && loadingMethodMaster) {
      const optValue = loadingMethodMaster.find((item) => item._id === text);
      return optValue?.loadingBy;
    } else {
      return text;
    }
  };

  const months: Month[] = [
    { title: "Jan", dataIndex: "jan" },
    { title: "Feb", dataIndex: "feb" },
    { title: "Mar", dataIndex: "mar" },
    { title: "Apr", dataIndex: "apr" },
    { title: "May", dataIndex: "may" },
    { title: "Jun", dataIndex: "jun" },
    { title: "Jul", dataIndex: "jul" },
    { title: "Aug", dataIndex: "aug" },
    { title: "Sep", dataIndex: "sep" },
    { title: "Oct", dataIndex: "oct" },
    { title: "Nov", dataIndex: "nov" },
    { title: "Dec", dataIndex: "dec" },
  ];
  const monthColumns: TableColumnsType<ReportChemicalVolume> = months.map((month) => ({
    title: month.title,
    dataIndex: "",
    render: (_, record) => {
      if (record && typeof record === "object") {
        const value = record[month.dataIndex];
        if (typeof value === "number") {
          return value ? <Text>{formatNumber(value)}</Text> : <Text>0</Text>;
        } else {
          return <Text>0</Text>;
        }
      }
    },
  }));
  const staticColumns: TableColumnsType<ReportChemicalVolume> = [
    {
      title: "No.",
      dataIndex: "no",
      width: 40,
      render: (text) => <Text>{text}</Text>,
    },
    {
      title: "Chemical Name",
      dataIndex: "chemicalName",
      key: "chemicalId",
      filters: mapFilter("chemicalId"),
      filteredValue: filter?.chemicalId || null,
      render: (text) => <Text>{text}</Text>,
    },
    {
      title: "Area",
      dataIndex: "area",
      filters: mapFilter("area"),
      filteredValue: filter?.area || null,
      render: (text) => <Text>{getMasterLabel("area", text)}</Text>,
    },
    {
      title: "Loading By",
      dataIndex: "loadingBy",
      key: "loadingById",
      filters: mapFilter("loadingById"),
      filteredValue: filter?.loadingById || null,
      render: (text) => <Text>{text}</Text>,
    },
    {
      title: "Unit",
      dataIndex: "unit",
      render: (text) => <Text>{text}</Text>,
    },
  ];
  const totalColumns: TableColumnsType<ReportChemicalVolume> = [
    {
      title: "Total",
      dataIndex: "total",
      width: 100,
      render: (value) => {
        if (typeof value === "number") {
          return value ? <Text>{formatNumber(value)} </Text> : <Text>0</Text>;
        } else {
          return <Text>0</Text>;
        }
      },
    },
  ];
  const columns: TableColumnsType<ReportChemicalVolume> = [...staticColumns, ...monthColumns, ...totalColumns];

  return (
    <div>
      {contextHolder}

      <div style={{ display: "flex", justifyContent: "space-between", paddingBottom: "10px" }}>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div style={{ paddingRight: "20px" }}>
            <Select
              value={selectedYear}
              style={{ width: 120 }}
              onChange={handleYearChange}
              options={optionYears.map((year) => ({ label: year, value: year }))}
              loading={loading}
              size="middle"
            />
          </div>

          <div>
            <Button
              size="middle"
              style={buttonStyle("table")}
              icon={<BsTable style={{ fontSize: "18px" }} />}
              onClick={() => handleButtonMenuClick("table")}
            />
            <Button
              size="middle"
              style={buttonStyle("chart")}
              icon={<PiChartBarFill style={{ fontSize: "18px" }} />}
              onClick={() => handleButtonMenuClick("chart")}
            />
          </div>
        </div>
      </div>

      {selectedButton === "table" && (
        <Table
          className="w-full whitespace-nowrap"
          columns={columns}
          dataSource={canViewTable(permissions) ? data : []}
          scroll={{ x: "max-content" }}
          loading={loading}
          onChange={onTableChange}
          pagination={{
            showSizeChanger: false,
            size: "small",
            current,
            pageSize,
            total,
          }}
        />
      )}
    </div>
  );
};
export default ReportChemicalVolumeTable;
